import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RESTAURANT_BY_ID } from "../constants";
import { addRequest, uid } from "../lib/storage";
import { isMain } from "../lib/permissions";
import { useTransferCart } from "../hooks/useTransferCart";
import TransferDirectionPicker from "./transfer/TransferDirectionPicker";
import TransferProductList from "./transfer/TransferProductList";

function buildTransferPayload({ user, fromR, toR, lines, notes }) {
  const nowIso = new Date().toISOString();
  return {
    id: uid("r"),
    type: "internal_transfer",
    restaurant: toR, // owner restaurant = destination
    fromRestaurant: fromR,
    toRestaurant: toR,
    vendor: null,
    requestedBy: user.id,
    requestedByName: user.name,
    requesterRole: user.role,
    requesterArea: user.area || null,
    items: lines.map((l) => ({
      productId: l.product.id,
      productName: l.product.name,
      unit: l.product.unit,
      quantity: l.qty,
      note: "",
    })),
    status: "submitted",
    notes,
    approvedBy: null,
    approvedAt: null,
    supplierType: "internal",
    createdAt: nowIso,
    updatedAt: nowIso,
  };
}

export default function TransferDialog({ open, onOpenChange, user, products, onCreated }) {
  const [fromR, setFromR] = useState("restaurant_1");
  const [toR, setToR] = useState("restaurant_2");
  const [query, setQuery] = useState("");
  const [notes, setNotes] = useState("");
  const { cart, entries, totalQty, inc, dec, remove, reset } = useTransferCart();

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setNotes("");
    reset();
    if (!isMain(user)) {
      setToR(user.restaurant);
      setFromR(user.restaurant === "restaurant_1" ? "restaurant_2" : "restaurant_1");
    } else {
      setFromR("restaurant_1");
      setToR("restaurant_2");
    }
  }, [open, user, reset]);

  const eligible = useMemo(() => {
    const list = products.filter((p) => p.active !== false && p.availableForTransfer !== false);
    const q = query.trim().toLowerCase();
    return !q ? list : list.filter((p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
  }, [products, query]);

  const lines = useMemo(
    () => entries
      .map((e) => ({ product: products.find((p) => p.id === e.pid), qty: e.qty }))
      .filter((l) => l.product),
    [entries, products]
  );

  const swapDirection = () => { const a = fromR; setFromR(toR); setToR(a); };

  const submit = () => {
    if (fromR === toR) return toast.error("Pick two different restaurants");
    if (lines.length === 0) return toast.error("Add at least one item");
    addRequest(buildTransferPayload({ user, fromR, toR, lines, notes }));
    toast.success(`Transfer requested: ${RESTAURANT_BY_ID[fromR]?.name} → ${RESTAURANT_BY_ID[toR]?.name}`);
    onCreated?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A] flex items-center gap-2">
            <ArrowRightLeft size={22} /> New internal transfer
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            Move stock between restaurants — no vendor involved.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2 space-y-4">
          <TransferDirectionPicker
            user={user}
            fromR={fromR}
            toR={toR}
            onFrom={setFromR}
            onTo={setToR}
            onSwap={swapDirection}
          />

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1F3A]/40" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products…"
              className="pl-9 h-10 bg-white border-[#E6E0D4]"
            />
          </div>

          <TransferProductList products={eligible} cart={cart} onInc={inc} onDec={dec} />

          {lines.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {lines.map(({ product, qty }) => (
                <div key={product.id} className="text-xs bg-white border border-[#E6E0D4] rounded-full pl-2.5 pr-1 py-1 flex items-center gap-1.5">
                  <span className="text-[#0B1F3A]">{product.name}</span>
                  <span className="text-[#0B1F3A]/50">· {qty} {product.unit}</span>
                  <button
                    onClick={() => remove(product.id)}
                    className="h-5 w-5 rounded-full hover:bg-[#F3EFE7] flex items-center justify-center text-[#0B1F3A]/50"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Pick-up time, who delivers, etc."
              className="mt-1 bg-white border-[#E6E0D4] min-h-[56px]"
            />
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={lines.length === 0}
            className="bg-[#0B1F3A] hover:bg-[#11294A] text-white disabled:opacity-50"
          >
            Request transfer • {totalQty} unit{totalQty !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
